import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { getHeaderLabel, SortSlotContext } from './StickyTable/utils'

export interface SorterResult {
  getDirection(field: string): string | null
  handleSortChange(field: string): void
  registerField(field: string, label: string): () => void
}

export function SortControl({ field }: { field: string }) {
  const { getDirection, handleSortChange, registerField } = useContext(SortContext)
  const direction = getDirection(field)
  const ref = useRef<HTMLDivElement>(null)

  // Registra la colonna per il menu "Ordina per" mostrato su mobile
  useLayoutEffect(() => {
    const th = ref.current?.closest('th')
    if (!th) return
    return registerField(field, getHeaderLabel(th))
  }, [field, registerField])

  return (
    <div ref={ref} className="ms-1 text-t-dark-grey pointer" style={{ whiteSpace: 'nowrap' }}>
      {(direction === 'up' || !direction) && (
        <span
          className="material-icons-outlined cursor-pointer"
          style={{ transform: 'rotate(90deg)', opacity: !direction ? 0.2 : 1 }}
          onClick={() => handleSortChange(field)}
        >
          switch_right
        </span>
      )}
      {direction === 'down' && (
        <span
          className="material-icons-outlined cursor-pointer"
          style={{ transform: 'rotate(90deg)' }}
          onClick={() => handleSortChange(field)}
        >
          switch_left
        </span>
      )}
    </div>
  )
}

function getSortData(sort: string | null) {
  if (!sort) {
    return {
      direction: null,
      sortedField: null,
    }
  }
  const isDown = sort.indexOf('-') === 0
  const sortedField = isDown ? sort.slice(1) : sort
  return {
    sortedField,
    direction: isDown ? 'down' : 'up',
  }
}

function useSorter(
  value: string | null = null,
  onSortChange: (sort: string) => void,
  registerField: SorterResult['registerField']
): SorterResult {
  const getDirection = useCallback(
    (field: string) => {
      const { direction, sortedField } = getSortData(value)
      if (sortedField === field) {
        return direction
      }
      return null
    },
    [value]
  )

  const handleSortChange = useCallback(
    (field: string) => {
      let nextSort = ''
      const { direction, sortedField } = getSortData(value)
      if (sortedField !== field) {
        nextSort = `-${field}`
      } else if (direction === 'down') {
        nextSort = field
      }
      onSortChange(nextSort)
    },
    [onSortChange, value]
  )

  return useMemo(
    () => ({
      getDirection,
      handleSortChange,
      registerField,
    }),
    [getDirection, handleSortChange, registerField]
  )
}

const SortContext = createContext<SorterResult>(null as never)

export interface SorterProps {
  children: ReactNode
  value: null | string
  onSortChange(sort: string): void
}

type SortField = { field: string; label: string }

function MobileSortSelect({
  fields,
  value,
  onSortChange,
}: {
  fields: SortField[]
  value: string | null
  onSortChange(sort: string): void
}) {
  const current = value ?? ''
  const options = fields.flatMap(({ field, label }) => [
    { value: field, label: `${label} (crescente)` },
    { value: `-${field}`, label: `${label} (decrescente)` },
  ])
  const isKnown = options.some((o) => o.value === current)

  return (
    <div className="d-flex align-items-center mb-2">
      <label className="label-m text-body-secondary me-2 text-nowrap" htmlFor="mobile-sort">
        Ordina per
      </label>
      <select
        id="mobile-sort"
        className="form-select"
        value={current}
        onChange={(e) => onSortChange(e.target.value)}
      >
        {!isKnown && <option value={current}>Predefinito</option>}
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  )
}

export default function Sorter({ value, onSortChange, children }: SorterProps) {
  const sortSlot = useContext(SortSlotContext)
  const [fields, setFields] = useState<SortField[]>([])

  const registerField = useCallback((field: string, label: string) => {
    setFields((prev) => [...prev.filter((f) => f.field !== field), { field, label }])
    return () => setFields((prev) => prev.filter((f) => f.field !== field))
  }, [])

  return (
    <SortContext.Provider value={useSorter(value, onSortChange, registerField)}>
      {children}
      {sortSlot &&
        fields.length > 0 &&
        createPortal(
          <MobileSortSelect fields={fields} value={value} onSortChange={onSortChange} />,
          sortSlot
        )}
    </SortContext.Provider>
  )
}
